import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import Cookies from 'js-cookie';
import { sso } from '../../../auth/sso';

const useStore = create(
  devtools((set, get) => ({
    cart: [],
    requests: [],
    draftMessages: [],
    approvedMessages: [],
    notificationData: { status: '' },
    SET_APPROVED_MESSAGES: (approvedMessages) => {
      set(() => ({ approvedMessages }));
    },
    SET_DRAFT_MESSAGES: (draftMessages) => {
      set(() => ({ draftMessages }));
    },
    SHOW_NOTIFICATION: (notificationData) => {
      set((state) => ({ notificationData: { ...state.notificationData, ...notificationData } }));
    },
    SAVE_REQUESTS: (requests = []) => {
      set((state) => ({ requests: [...state.requests, ...requests] }));
    },
    UPDATE_REQUESTS: (request) => {
      set((state) => {
        const index = state.requests.findIndex((r) => r.id === request.id);
        if (index !== -1) {
          state.requests[index] = request;
        }
        return { requests: [...state.requests] };
      });
    },
    SET_ADDRESS: (addressVal) => {
      set(() => ({ address: addressVal }));
    },
    LOAD_FROM_COOKIE: () => {
      if (!(Cookies.get('cart') == null)) {
        set(() => ({ cart: JSON.parse(Cookies.get('cart')) }));
      }
    },
    ADD_ITEM: (item) => {
      if (get().cart.filter((cartItem) => cartItem.FormNumber == item.FormNumber).length > 0) {
        const index = get().cart.findIndex((cartItem) => cartItem.FormNumber === item.FormNumber);
        const tempArray = [...get().cart];
        tempArray[index].Quantity = String(parseInt(get().cart[index].Quantity) + 1);
        set(() => ({
          cart: tempArray,
        }));
        Cookies.set('cart', JSON.stringify(get().cart), {
          path: '/Forms',
          secure: true,
          sameSite: 'strict',
        });
      } else {
        set((state) => ({ cart: [...state.cart, item] }));
        Cookies.set('cart', JSON.stringify(get().cart), {
          path: '/Forms',
          secure: true,
          sameSite: 'strict',
        });
      }
    },
    ADD_ITEMS: (items) => {
      set((state) => ({ cart: [...state.cart, ...items] }));
    },
    REMOVE_ITEM: (formNumber) => {
      set((state) => ({
        cart: state.cart.filter((cartItem) => cartItem.FormNumber !== formNumber),
      }));
      Cookies.set('cart', JSON.stringify(get().cart), {
        path: '/Forms',
        secure: true,
        sameSite: 'strict',
      });
    },
    SET_QUANTITY: (formNumber, quantity) => {
      const index = get().cart.findIndex((cartItem) => cartItem.FormNumber === formNumber);
      const tempArray = [...get().cart];
      tempArray[index].Quantity = quantity;
      set(() => ({
        cart: tempArray,
      }));
      Cookies.set('cart', JSON.stringify(get().cart), {
        path: '/Forms',
        secure: true,
        sameSite: 'strict',
      });
    },
    CLEAR_CART: () => {
      set({ cart: [] });
      Cookies.set('cart', JSON.stringify(get().cart), {
        path: '/Forms',
        secure: true,
        sameSite: 'strict',
      });
    },
    CHECKOUT: async (address, name) => {
      let formSubmissionResponse = await submitOrder(get().cart, address, name);
      if (formSubmissionResponse.status == 200) {
        set({ cart: [] });
        Cookies.set('cart', JSON.stringify(get().cart), {
          path: '/Forms',
          secure: true,
          sameSite: 'strict',
        });
        return formSubmissionResponse;
      } else {
        throw new Error(formSubmissionResponse.status);
      }
    },
  }))
);

const updateAddress = async (address) => {
  const body = {
    Address1: address.Address1,
    Address2: address.Address2,
    City: address.City,
    State: address.State,
    Zip: address.Zip,
    UserID: 0,
  };
  const options = {
    crossDomain: true,
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };

  let response = await fetch('/forms/UpdateAddress', options);
  return response;
};

const submitOrder = async (forms, address, name) => {
  const accessToken = await sso.getToken();
  if (!accessToken) {
    // Ask the guard where to go rather than assuming "log in". During a sign-out the
    // token is gone by design and a redirect is already in flight, so minting an
    // authorize url here would supersede it and silently restore the session. The
    // guard returns the logout url in that case and the authorize url otherwise.
    const result = await sso.checkAuthenticated(window.location.href);
    if (!result.authenticated) {
      window.location.assign(result.redirectUrl);
    }
    // Redirecting away — never-resolving promise so callers don't act on a half-built
    // request during navigation.
    return new Promise(() => {});
  }

  const body = {
    Forms: forms,
    Address1: address.Address1,
    Address2: address.Address2,
    City: address.City,
    State: address.State,
    Zip: address.Zip,
    SendTo: name,
    UserID: 0,
  };
  const options = {
    crossDomain: true,
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  };

  let response = await fetch('/forms/CreateFormsOrder', options);

  // A 401 here means the session was revoked server-side (not merely "no
  // token") — hand it to sso.onUnauthorized() for the silent-refresh /
  // bounded-streak re-auth policy before the caller sees the failed response.
  if (response.status === 401) {
    await sso.onUnauthorized();
  }

  return response;
};

const cancelOrder = (orderID) => {
  const JSONParam = { OrderID: orderID, StatusID: '4' };
  const queryParams = {
    crossDomain: true,
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(JSONParam),
  };

  fetch('forms/CancelFormsOrder', queryParams).then((res) => console.log(res.json()));
};

export { useStore };
